import { defineStore } from 'pinia'
import { getContentReviewProfiles } from './api'
import type { ReviewCriteriaProfile, CriteriaItem } from './api'
import { useContentProfileStore } from '@/contentprofile/contentprofile.store'
import { useCustomizationDemoStore } from '@/customization-demo/customization-demo.store'
import { CUSTOMIZATIONS } from '@/customization-demo/customization_demo_catalog'

// Check if a criteria item matches the release mode (beta or released)
function criteriaMatchesRelease(item: CriteriaItem, betaMode: boolean): boolean {
  // Default to released if not specified
  const isReleased = item.released ?? true
  const isBetaOnly = item.beta_only ?? false
  return betaMode ? isReleased || isBetaOnly : isReleased && !isBetaOnly
}

function findProfileCriteria(
  profiles: ReviewCriteriaProfile[],
  profileId: string,
  contentType: string,
  betaMode: boolean
): CriteriaItem[] {
  const normalizedId = profileId.toLowerCase()

  // Find matching profile (without release filtering at profile level)
  let profile = profiles.find(
    (p) => p.content_profile.toLowerCase() === normalizedId
  )

  // Filter criteria items based on individual released/beta_only flags
  let criteria = profile?.criteria_by_type[contentType] ?? []
  criteria = criteria.filter(item => criteriaMatchesRelease(item, betaMode))

  // Fall back to default profile if no matching profile or no visible criteria
  if (criteria.length === 0) {
    const defaultProfile = profiles.find((p) => p.content_profile === '')
    const defaultCriteria = defaultProfile?.criteria_by_type[contentType] ?? []
    criteria = defaultCriteria.filter(item => criteriaMatchesRelease(item, betaMode))
  }

  return criteria
}

export const useContentReviewStore = defineStore('contentReview', {
  state: () => ({
    profiles: [] as ReviewCriteriaProfile[],
    loading: false,
    error: null as string | null,
  }),

  actions: {
    async fetchProfiles() {
      if (this.loading) return
      this.loading = true
      this.error = null
      try {
        this.profiles = await getContentReviewProfiles()
      } catch (err) {
        console.error('Failed to fetch content review profiles:', err)
        this.error = 'Failed to load review criteria'
        this.profiles = []
      } finally {
        this.loading = false
      }
    },
  },

  getters: {
    // The profile auto-review runs under. A Customization Demo overrides the account's profile for
    // review alone — standards, subjects and statement generation keep the account's profile — the
    // way toolkit-spa's autoReviewProfileId does. Every review surface reads this, not the content
    // profile store, so a demo picked in the modal applies to the page as soon as it is chosen.
    reviewProfileId(): string {
      const demoProfileId = useCustomizationDemoStore().demoProfileFor(CUSTOMIZATIONS.AUTO_REVIEW)
      return demoProfileId || useContentProfileStore().profileId
    },
    criteriaForProfile(state) {
      return (profileId: string, contentType: string, betaMode: boolean): CriteriaItem[] =>
        findProfileCriteria(state.profiles, profileId, contentType, betaMode)
    },
    impactCriteriaForProfile(state) {
      return (profileId: string, betaMode: boolean): CriteriaItem[] =>
        findProfileCriteria(state.profiles, profileId, 'impact', betaMode)
    },
    presentLevelsCriteriaForProfile(state) {
      return (profileId: string, betaMode: boolean): CriteriaItem[] =>
        findProfileCriteria(state.profiles, profileId, 'presentlevels', betaMode)
    },
    criteriaSetName(state) {
      return (profileId: string, _betaMode: boolean): string => {
        const normalizedId = profileId.toLowerCase()
        const profile = state.profiles.find(
          (p) => p.content_profile.toLowerCase() === normalizedId
        )
        return profile?.criteria_set_name ?? ''
      }
    },
    // Returns the criteria set name for a profile, considering content type and beta mode
    // Falls back to default profile's name if custom profile has no visible criteria
    criteriaSetNameForContentType(state) {
      return (profileId: string, contentType: string, betaMode: boolean): string => {
        const normalizedId = profileId.toLowerCase()

        // Find custom profile
        const customProfile = state.profiles.find(
          (p) => p.content_profile.toLowerCase() === normalizedId
        )

        // Check if custom profile has visible criteria for this content type and beta mode
        if (customProfile) {
          const customCriteria = customProfile.criteria_by_type[contentType] ?? []
          const visibleCustomCriteria = customCriteria.filter(item => criteriaMatchesRelease(item, betaMode))
          if (visibleCustomCriteria.length > 0) {
            return customProfile.criteria_set_name
          }
        }

        // Fall back to default profile's name
        const defaultProfile = state.profiles.find((p) => p.content_profile === '')
        return defaultProfile?.criteria_set_name ?? ''
      }
    },
    isCustomCriteriaSet(state) {
      return (profileId: string, _betaMode: boolean): boolean => {
        if (!profileId) return false
        const normalizedId = profileId.toLowerCase()
        return state.profiles.some(
          (p) =>
            p.content_profile !== '' &&
            p.content_profile.toLowerCase() === normalizedId
        )
      }
    },
  },
})
